/**
 * MOCK · CANONICAL (CT-5277) — the Unified Profile types a tenant has published.
 *
 * The seed exists to prove the epic's central claim: one set of components
 * renders any entity type. So it deliberately covers the awkward cases, not
 * just the happy one.
 *
 *   customers    the fat, fully-mapped type with several pages of records
 *   employees    a second real type whose rows use camelCase aliases
 *   vehicles     thinly mapped — no description, no badges, no visual
 *   dealerships  published but empty, for the empty state
 *   salaries     NOT returned by the list: the caller has no access to it, and
 *                asking for it directly is what produces the 403
 */

/**
 * `visual` is null across the board on purpose.
 *
 * The first version pointed these at `https://img.aims.local/...`, a host that
 * resolves nowhere. Every test still passed — and the hub rendered two empty
 * circles in the browser, because `Avatar` shows a broken `<img>` rather than
 * falling back to initials when a `src` fails to load. A mock that makes a
 * screen look broken in QA is a bug in the mock.
 *
 * Until a real image source exists, no visual means the initials fallback runs,
 * which is the path these types take in production anyway.
 */
const SEED_TYPES = [
  ['customers', 'Customers', null, 24],
  ['employees', 'Employees', null, 8],
  ['companies', 'Companies', null, 5],
  ['vehicles', 'Vehicles', null, 3],
  ['dealerships', 'Dealerships', null, 0],
];

/** Published, but off-limits to the current caller. Never appears in the list. */
export const FORBIDDEN_TYPE_IDS = Object.freeze(['salaries']);

const buildSeed = () =>
  SEED_TYPES.map(([entityType, label, visual, count]) => ({
    entity_type: entityType,
    label,
    visual,
    count,
  }));

let mockProfileTypes = buildSeed();

export const getMockProfileTypes = () => mockProfileTypes;
export const setMockProfileTypes = (next) => {
  mockProfileTypes = next;
};

/** A record was created: the type's published count follows it. */
export const bumpMockProfileTypeCount = (entityType) => {
  mockProfileTypes = mockProfileTypes.map((type) =>
    type.entity_type === entityType ? { ...type, count: (type.count ?? 0) + 1 } : type,
  );
};

/** Test-only: reset the mutable seed between tests within a file. */
export const __resetMockProfileTypesForTests = () => {
  mockProfileTypes = buildSeed();
};
